import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import moment from "moment-timezone";
import Sheet from "../../../../../../components/shared/Sheet";
import OfferShifts from "./OfferShifts";
import SwapShifts from "./SwapShifts";
import OpenShifts from "./OpenShifts";
import SelfSchedulingShifts from "./SelfSchedulingShifts";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import useApi from "../../../../../../utils/api";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import { useResultDialog } from "../ShiftTrade/ResultDialog/useResultDialog";
import { cn } from "../../../../../../lib/utils";
import type { ShiftResult } from "../ShiftTrade/ResultDialog/types";

const getShiftResults = (responses: unknown): ShiftResult[] => {
    const entries = Array.isArray(responses) ? responses : [responses];
    return entries
        .flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
        .filter((entry: any): entry is ShiftResult => (
            typeof entry?.success === "boolean" && Boolean(entry?.shift || entry?.request)
        ));
};

function Requests({ isOpen, close, approve, deny, initialMenu, focusedRequest }: any) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const [menu, setMenu] = useState("offer-shifts");

    useEffect(() => {
        if (isOpen && initialMenu) {
            setMenu(initialMenu);
        }
    }, [isOpen, initialMenu]);
    const [filters] = useFilters();
    const { type } = useScheduleContext();
    const { authGet } = useApi();
    const { open: openResultDialog, modal: resultDialog } = useResultDialog({ onClose: close });

    const swapShiftsQuery = useQuery({
        queryKey: ["/request/my-team", {
            namespace: "schedule",
            pagination: "false",
            "filter[type][method]": "exact",
            "filter[type][value]": "swap-shifts",
            "filter[status][method]": "exact",
            "filter[status][value]": "pending",
            startDate: filters?.mode === "month" ? moment(filters?.from).startOf("week").format("YYYY-MM-DD") : filters?.from,
            endDate: filters?.mode === "month" ? moment(filters?.to).endOf("week").format("YYYY-MM-DD") : filters?.to,
        }],
        queryFn: ({ queryKey }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params });
        },
        enabled: ["home", "my-profile"].includes(type)
    });

    const title = useMemo(() => {
        return (
            <div className="tw-flex tw-items-center tw-gap-7">
                <span>{t("requests")}</span>
                <span className="tw-text-sm tw-font-normal">
                    {"("}
                    {moment(filters?.from).format(dateFormat)}
                    {" - "}
                    {moment(filters?.to).format(dateFormat)}
                    {")"}
                </span>
            </div>
        );
    }, [filters?.from, filters?.to, dateFormat, t]);

    const approveRequest = useCallback((request: any) => {
        approve({
            ...request,
            close: (responses: unknown) => {
                request?.close?.();

                const results = getShiftResults(responses);
                if (results.length > 0) {
                    openResultDialog(results);
                }
            }
        });
    }, [approve, openResultDialog]);

    const content = useMemo(() => {
        switch (menu) {
            case "offer-shifts":
                return (
                    <OfferShifts
                        approve={approveRequest}
                        deny={deny}
                    />
                );
            case "open-shifts":
                return <OpenShifts focusedRequest={focusedRequest} />;
            case "self-scheduling":
                return <SelfSchedulingShifts />;
            case "swap-shifts":
            default:
                return (
                    <SwapShifts
                        approve={approveRequest}
                        deny={deny}
                        loading={swapShiftsQuery.isLoading}
                        data={swapShiftsQuery.data?.result || []}
                        totalItems={swapShiftsQuery.data?.totalItems || 0}
                        refetch={swapShiftsQuery.refetch}
                    />
                );
        }
    }, [menu, approveRequest, deny, swapShiftsQuery, focusedRequest]);

    const onClose = useCallback(() => {
        setMenu("offer-shifts");
        close();
    }, [close]);

    return (
        <Sheet open={isOpen} close={onClose} title={title}>
            <div className="tw-flex tw-items-center">
                <span
                    className={cn(
                        menu === "offer-shifts" && "tw-text-primary tw-border-b-2 tw-border-primary tw-font-semibold",
                        "tw-text-sm tw-py-2 tw-px-3 tw-cursor-pointer"
                    )}
                    onClick={() => setMenu("offer-shifts")}
                >
                    {t("offer-shifts")}
                </span>
                <span
                    className={cn(
                        menu === "swap-shifts" && "tw-text-primary tw-border-b-2 tw-border-primary tw-font-semibold",
                        "tw-text-sm tw-py-2 tw-px-3 tw-cursor-pointer"
                    )}
                    onClick={() => setMenu("swap-shifts")}
                >
                    {t("shift-replacement")}
                </span>
                <span
                    className={cn(
                        menu === "open-shifts" && "tw-text-primary tw-border-b-2 tw-border-primary tw-font-semibold",
                        "tw-text-sm tw-py-2 tw-px-3 tw-cursor-pointer"
                    )}
                    onClick={() => setMenu("open-shifts")}
                >
                    {t("open-shifts")}
                </span>
                {/* <span
                    className={cn(
                        menu === "self-scheduling" && "tw-text-primary tw-border-b-2 tw-border-primary tw-font-semibold",
                        "tw-text-sm tw-py-2 tw-px-3 tw-cursor-pointer"
                    )}
                    onClick={() => setMenu("self-scheduling")}
                >
                    {t("self-scheduling")}
                </span> */}
            </div>

            <div className="tw-p-3">
                <div className="tw-overflow-auto tw-mb-2 tw-h-[calc(100vh-15rem)]">
                    {content}
                </div>
            </div>

            {resultDialog}
        </Sheet>
    );
}

export default Requests;
